import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'
import { AttendancePolicyEmployeesCollectionListDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import AddEmployeesModal from './_AddEmployeesModal'
import TransferToPolicyModal from './_TransferToPolicyModal'

interface Props {
  attendancePolicy: AttendancePolicy
  attendanceSetting: AttendanceSetting
}

const managedByEmployeeEnum = {
  managed_by_me: 'managed_by_me',
  managed_by_others: 'managed_by_others',
  managed_directly_by_me: 'managed_directly_by_me',
  managed_timeoff_by_me: 'managed_timeoff_by_me',
}

const PolicyEmployees = ({ attendancePolicy, attendanceSetting }: Props) => {
  const { company, employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const [showChangePolicyModal, setShowChangePolicyModal] = React.useState(false)
  const [changePolicyEmployee, setChangePolicyEmployee] = React.useState<{
    id: string
    name: string
  }>({ id: '0', name: '' })
  const [showEmployeeModal, setShowEmployeeModal] = React.useState(false)
  const navigate = useNavigate()

  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()

  const faceRecognitionFeatureEnabled = useTimeTrackingFeatureAvailability(
    'time_tracking_face_recognition'
  )
  const currentEmployeeId = currentEmployee?.id

  const faceRecognitionEnabled =
    faceRecognitionFeatureEnabled && attendanceSetting.attributes.face_recognition

  const source = useGraphqlDataCollectionSource(
    {
      document: AttendancePolicyEmployeesCollectionListDocument,
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const locationIds = filters.workplace?.map((id) => id)
        const jobLevelIds = !hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id) => id.toString())
          : undefined
        const jobCatalogTreeNodeIds = hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id) => id.toString())
          : undefined
        const onlyMyReportees =
          filters.directReports &&
          filters.directReports.length > 0 &&
          (filters.directReports.includes(managedByEmployeeEnum.managed_by_me) ||
            filters.directReports.includes(managedByEmployeeEnum.managed_by_others))
            ? filters.directReports?.includes(managedByEmployeeEnum.managed_by_me)
            : undefined
        const directReports =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes(managedByEmployeeEnum.managed_directly_by_me)
            ? true
            : undefined
        const managedTimeoffByMe =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes(managedByEmployeeEnum.managed_timeoff_by_me)
            ? [currentEmployeeId ?? '0']
            : undefined

        return {
          companyId: company.id,
          onlyMyReportees,
          reportingTo: filters.reportingTo?.map((id) => id),
          onlyDirectReports: directReports,
          timeoffManagerIds: managedTimeoffByMe,
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          onlyActive: filters.active?.includes(true),
          policyId: attendancePolicy.id,
          sort: sortings?.[0],
          search: search,
          attendancePolicyId: attendancePolicy.id,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          faceRecognitionEnabled: faceRecognitionEnabled,
        }
      },
      mapPagination: (data) => {
        return {
          total: data.employees.employeesConnection.totalCount ?? 0,
        }
      },
      mapData: (data) => data.employees.employeesConnection.nodes,
      itemUrl: (item) => navigation.employees.id.fullPath({ id: item.id }),
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        'referenceContract.jobTitle': {
          label: i18n.t('common.job'),
        },
        startsOn: {
          label: i18n.t('employee_form.hire_date'),
        },
        active: {
          label: i18n.t('employee_form.status'),
        },
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      filters: {
        team: employeeFilters.team,
        workplace: employeeFilters.workplace,
        active: {
          type: 'in',
          label: i18n.t('time_tracking.settings.employees.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.settings.employees.filters.only_active'),
                value: true,
              },
            ],
          },
        },
        jobRole: employeeFilters.jobRole,
        directReports: {
          type: 'in',
          label: i18n.t('time_tracking.settings.employees.filters.managed_by_me'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.settings.employees.filters.managed_by_me_option'),
                value: managedByEmployeeEnum.managed_by_me,
              },
              {
                label: i18n.t('time_tracking.settings.employees.filters.managed_by_others'),
                value: managedByEmployeeEnum.managed_by_others,
              },
              {
                label: i18n.t(
                  'time_tracking.settings.employees.filters.managed_directly_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_directly_by_me,
              },
              {
                label: i18n.t(
                  'time_tracking.settings.employees.filters.managed_timeoff_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_timeoff_by_me,
              },
            ],
          },
        },
        reportingTo: employeeFilters.reportingTo,
      },
      primaryActions: () => ({
        label: i18n.t('time_tracking.settings.employees.add.cta'),
        icon: Add,
        onClick: () => setShowEmployeeModal(true),
      }),
      selectable: (item) => item.id,
      itemActions: (item) => [
        {
          label: i18n.t('time_tracking.settings.employees.actions.transfer'),
          icon: Pencil,
          onClick: () => {
            setShowChangePolicyModal(true)
            setChangePolicyEmployee({ id: item.id, name: item.fullName })
          },
        },
        ...(faceRecognitionEnabled
          ? [
              {
                label: i18n.t('time_tracking.settings.employees.actions.view_logs'),
                icon: EyeVisible,
                onClick: () =>
                  navigate(
                    navigation.settings.time_tracking.settingId.employeesEmployeeIdFaceRecognitionLogs.fullPath(
                      {
                        settingId: attendanceSetting.attributes.id,
                        employeeId: item.id,
                      }
                    )
                  ),
              },
            ]
          : []),
      ],
    },
    [company.id, attendancePolicy.id, hasJobCatalogTreeFeature]
  )

  return (
    <>
      {showChangePolicyModal && (
        <TransferToPolicyModal
          attendancePolicy={attendancePolicy}
          onClose={() => setShowChangePolicyModal(false)}
          employeeData={changePolicyEmployee}
        />
      )}
      {showEmployeeModal && (
        <AddEmployeesModal
          policyEmployeesIds={attendancePolicy.attributes.employees}
          setShowModal={setShowEmployeeModal}
          attendancePolicyId={attendancePolicy.id}
          onClose={() => setShowEmployeeModal(false)}
        />
      )}
      <ResponsivePage.Body>
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('time_tracking.settings.employees.first_name'),
                    sorting: 'firstName',
                    render: (item) => ({
                      type: 'person',
                      value: {
                        firstName: item.firstName,
                        lastName: '',
                        avatar: item.access.avatar,
                      },
                    }),
                  },
                  {
                    label: i18n.t('time_tracking.settings.employees.last_name'),
                    sorting: 'lastName',
                    render: (item) => item.lastName,
                  },
                  {
                    label: i18n.t('time_tracking.settings.employees.job'),
                    render: (item) => item.referenceContract?.jobTitle ?? '',
                  },
                  {
                    label: i18n.t('time_tracking.settings.employees.hire_date'),
                    render: (item) => item.referenceContract?.startsOn ?? '',
                  },
                  {
                    label: i18n.t('time_tracking.settings.employees.status_name'),
                    render: (item) => ({
                      type: 'status',
                      value: {
                        label: item.active
                          ? i18n.t('time_tracking.settings.employees.status.active')
                          : i18n.t('time_tracking.settings.employees.status.inactive'),
                        status: item.active ? 'positive' : 'neutral',
                      },
                    }),
                  },
                  ...(faceRecognitionEnabled
                    ? [
                        {
                          label: i18n.t('time_tracking.settings.employees.picture_count'),
                          // @ts-expect-error: contextual typing lost in conditional spread, safe to ignore implicit any
                          render: (item) =>
                            item.faceRecognitionEmployeesPicturesConnection.totalCount ?? 0,
                        },
                      ]
                    : []),
                ],
              },
            },
          ]}
        />
      </ResponsivePage.Body>
    </>
  )
}

export default PolicyEmployees
